import { useAuth0 } from "@auth0/auth0-react";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import dayjs from "dayjs";
import { sumBy } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { Box } from "@/Components/v2/atoms/Box";
import { IconButton } from "@/Components/v2/atoms/Button";
import { OutlinedButton, PrimaryButton } from "@/Components/v2/atoms/Button";
import { ToggleButton } from "@/Components/v2/atoms/Button";
import { Dialog } from "@/Components/v2/atoms/Dialog";
import { Divider } from "@/Components/v2/atoms/Divider";
import { FormControl } from "@/Components/v2/atoms/FormControl";
import { MenuItem } from "@/Components/v2/atoms/MenuItem";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { ToggleButtonGroup } from "@/Components/v2/molecule/ButtonGroup";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useConfig } from "@/hooks/useConfig";
import { ABBREVIATED_OPTION } from "@/hooks/useConfig";
import { CreateScenarioResponse } from "@/pages/scenario-builder/v3/api/createScenario";
import { useCreateScenario } from "@/pages/scenario-builder/v3/api/createScenario";
import { useModelMetadata } from "@/pages/scenario-builder/v3/api/getCustomerModelMetadata";
import { useDateRanges } from "@/pages/scenario-builder/v3/api/getDateRanges";
import { useHistoricalSpends } from "@/pages/scenario-builder/v3/api/getHistoricalSpend";
import { useRunScenario } from "@/pages/scenario-builder/v3/api/runScenario";
import { useUpdateScenarioV3 } from "@/pages/scenario-builder/v3/api/updateScenario";
import {
  ScenarioType,
  ScenarioTypeEnum,
} from "@/pages/scenario-builder/v3/pages/scenario-form/types";
import { allocateForecastBudget } from "@/pages/scenario-builder/v3/pages/scenario-form/utils/logic";
import { isDefined } from "@/Utils/array-utils";

import { ScenarioSummary } from "./ScenarioSummary";
import { StyledSelect, StyledSlider } from "./StyledQuickSimulateFormComponents";
import { useBudgetCalculation } from "./utils/useBudgetCalculation";
export interface QuickSimulateFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onSimulationComplete?: () => void;
  defaultBusinessUnit?: string;
}

export const QuickSimulateForm: React.FC<QuickSimulateFormProps> = ({
  open,
  onClose,
  onSubmit,
  onSimulationComplete,
  defaultBusinessUnit,
}) => {
  const { value: isScenariosApiEnabled } = useFeatureGate(featureFlag.scenarioBuilderApi);
  const [months, setMonths] = useState(6); // Default 6 months
  const [budgetPercentage, setBudgetPercentage] = useState(0); // Default 0% (current budget)
  const [businessUnit, setBusinessUnit] = useState("");
  const [scenarioType, setScenarioType] = useState<"budget" | "optimizer">("budget");
  const [budgetMetricType, setBudgetMetricType] = useState<"percentage" | "amount">("percentage");

  const { getBCPHierarchyLabel } = useDefaultNodeNameInfo();
  const { scenarioCreated, scenarioSubmitted } = useAnalytics();
  const { user } = useAuth0();
  const { config } = useConfig();
  const formatCurrency = config.currency.formatCurrency;
  const theme = useTheme();

  // Quick Scenario business logic hooks
  const { data: metaData, isLoading: isLoadingMetadata } = useModelMetadata({
    config: {
      refetchOnWindowFocus: false,
    },
    isScenariosApiEnabled,
  });

  const brandGeographies = metaData?.brand_geographies;

  const { data: dateRanges } = useDateRanges({
    config: {
      refetchOnWindowFocus: false,
    },
    isScenariosApiEnabled,
  });

  // State for Quick Scenario parameters to track changes
  const [quickSimulateParams, setQuickSimulateParams] = useState<{
    businessUnit: string | null;
    months: number;
    budgetPercentage: number;
  }>({
    businessUnit: null,
    months: 6,
    budgetPercentage: 0,
  });

  // Calculate dynamic date range based on selected months
  const quickSimulateStartDate = useMemo(() => {
    if (!dateRanges) return null;
    return dayjs.utc(dateRanges.startDate).add(1, "month").startOf("month");
  }, [dateRanges]);

  const quickSimulateEndDate = useMemo(() => {
    if (!quickSimulateStartDate) return null;
    return quickSimulateStartDate
      .add(quickSimulateParams.months, "month")
      .subtract(1, "day")
      .endOf("month");
  }, [quickSimulateStartDate, quickSimulateParams.months]);

  // Historical spends for budget calculation - refetch when business unit or dates change
  const { data: historicalSpendsDTO } = useHistoricalSpends({
    config: {
      refetchOnWindowFocus: false,
      enabled:
        !!quickSimulateParams.businessUnit && !!quickSimulateStartDate && !!quickSimulateEndDate,
    },
    startDate: quickSimulateStartDate?.format("YYYY-MM-DD"),
    endDate: quickSimulateEndDate?.format("YYYY-MM-DD"),
    brand: quickSimulateParams.businessUnit || undefined,
    geographies: undefined,
    isScenariosApiEnabled,
  });

  // Calculate historical spend base for budget calculation
  const historicalSpendBase = useMemo(() => {
    if (!historicalSpendsDTO || historicalSpendsDTO.length === 0) {
      return 0;
    }

    return sumBy(historicalSpendsDTO, (spend) => spend.marketing_spend.spend);
  }, [historicalSpendsDTO]);

  // Calculate budget amounts using custom hook
  const { budgetAmount, maxAllowedBudget } = useBudgetCalculation({
    historicalSpendBase,
    budgetPercentage,
    historicalSpendsDTO,
    brand: businessUnit || undefined,
    geography: undefined, // Quick Scenario uses all geographies (undefined = all)
  });

  // Quick Simulate mutations
  const quickSimulateCreateScenario = useCreateScenario({
    config: {
      onSuccess: (result: CreateScenarioResponse) => {
        if (result.uuid) {
          scenarioCreated({
            scenarioType: ScenarioType.toString(result.scenarioType as ScenarioTypeEnum),
            scenarioId: result.uuid,
            scenarioName: result.name || "Untitled Scenario",
            isCloned: false,
            isQuickScenario: true,
          });
        }
        // No navigation for Quick Simulate
      },
    },
    isScenariosApiEnabled,
  });

  const runScenario = useRunScenario({ isScenariosApiEnabled });
  const updateScenario = useUpdateScenarioV3({ isScenariosApiEnabled });

  // Set default business unit when brandGeographies becomes available
  useEffect(() => {
    if (!brandGeographies?.length || businessUnit) {
      return;
    }

    let selectedBusinessUnit: string | null = null;

    // If defaultBusinessUnit exists and is in the list, use it
    if (defaultBusinessUnit) {
      const defaultExists = brandGeographies.some((bg) => bg.brand === defaultBusinessUnit);
      if (defaultExists) {
        selectedBusinessUnit = defaultBusinessUnit;
      }
    }

    // Otherwise, use the first business unit in the list
    if (!selectedBusinessUnit && brandGeographies[0]) {
      selectedBusinessUnit = brandGeographies[0].brand;
    }

    if (selectedBusinessUnit) {
      setBusinessUnit(selectedBusinessUnit);
      setQuickSimulateParams({
        businessUnit: selectedBusinessUnit,
        months,
        budgetPercentage,
      });
    }
  }, [brandGeographies, businessUnit, defaultBusinessUnit, months, budgetPercentage]);

  const handleMonthsChange = (_: Event, newValue: number | number[]) => {
    const newMonths = newValue as number;
    setMonths(newMonths);
    setQuickSimulateParams((prev) => ({
      ...prev,
      months: newMonths,
    }));
  };

  const handleBudgetChange = (_: Event, newValue: number | number[]) => {
    const newBudgetPercentage = Math.max(newValue as number, -95);
    setBudgetPercentage(newBudgetPercentage);
    setQuickSimulateParams((prev) => ({
      ...prev,
      budgetPercentage: newBudgetPercentage,
    }));
  };

  const handleBusinessUnitChange = (value: string) => {
    setBusinessUnit(value);
    setQuickSimulateParams((prev) => ({
      ...prev,
      businessUnit: value,
    }));
  };

  // Handle Quick Simulate - moved from ScenariosPage
  const handleQuickSimulate = async () => {
    try {
      // Handle modal close, navigation
      onSubmit();

      // Use the pre-calculated start and end dates
      const startDate = quickSimulateStartDate!;
      const endDate = quickSimulateEndDate!;

      // Use calculated budget amount from hook
      const budgetAmountForScenario = budgetAmount;

      // Generate scenario name with new format: "Business Unit, Budget, Date Range"
      const scenarioTypeName = scenarioType === "budget" ? "Forecast" : "Optimizer";

      const startDateFormatted = startDate.format("Do MMM YYYY");
      const endDateFormatted = endDate.format("Do MMM YYYY");
      const budgetFormatted = formatCurrency(budgetAmount, ABBREVIATED_OPTION);

      // Get formatted business unit label
      const businessUnitLabel = getBCPHierarchyLabel({
        brand: businessUnit,
        category: "default",
        product: "default",
      });

      const scenarioName = `${businessUnitLabel}, ${budgetFormatted}, ${startDateFormatted} - ${endDateFormatted}`;

      // Show toast notification
      toast.info("Quick scenario simulating");

      // Create new scenario with basic info
      const newScenario = await quickSimulateCreateScenario.mutateAsync({
        scenarioType: scenarioType,
        createdBy: user!.email!,
        name: scenarioName,
        scenarioCount: scenarioType === "optimizer" ? 1000 : null,
      });

      if (!newScenario.uuid) {
        throw new Error("Failed to create scenario - no UUID returned");
      }

      // Calculate allocated spends for budget allocation (needed for forecast run request)
      // historicalSpendsDTO is already filtered by the selected business unit
      const allocatedSpends = allocateForecastBudget(
        historicalSpendsDTO!,
        budgetAmountForScenario,
        [] // No constraints
      );

      // Build scenario constraints - empty for Quick Simulate scenarios (no spend limits)
      const scenarioConstraints: any[] = [];

      // Update the scenario with all required fields
      const updateScenarioRequest = {
        name: scenarioName,
        description: `Quick ${scenarioTypeName}: ${businessUnitLabel}, ${budgetFormatted} Budget, ${startDateFormatted} - ${endDateFormatted}`,
        brand: businessUnit,
        geography: null, // Use all geographies by default
        spendGeographicGranularity: "all_geographies" as const,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        value: budgetAmountForScenario,
        scenarioConstraints,
      };

      await updateScenario.mutateAsync({
        scenarioUUID: newScenario.uuid,
        scenario: updateScenarioRequest,
        scenarioType,
      });

      // Run the scenario with proper request structure
      const runRequest =
        scenarioType === "optimizer"
          ? {
              type: "optimizer" as const,
              optimizerMediaConstraints: [], // No constraints for optimizer
            }
          : {
              type: "budget" as const,
              allocatedSpends:
                allocatedSpends?.map((spend) => ({
                  geography: spend.geography,
                  spend: spend.spend,
                  channel: spend.channel,
                  product: spend.product,
                  category: spend.category,
                  brand: spend.brand,
                })) || [],
            };

      await runScenario.mutateAsync({
        scenarioId: newScenario.uuid,
        runScenarioRequest: runRequest,
      });

      // Track scenario submission with quick scenario parameters
      scenarioSubmitted({
        scenarioName: scenarioName,
        scenarioId: newScenario.uuid,
        scenarioType: scenarioType,
        budgetRatio: budgetPercentage,
        scenarioLengthMonths: months,
        mediaSpendInputs: scenarioType === "budget" ? 0 : undefined,
        mediaSpendConstraints: scenarioType === "optimizer" ? 0 : undefined,
        isQuickScenario: true,
        version: 3,
      });

      // Success feedback
      toast.success(
        `Quick ${scenarioTypeName} simulation started! Check the scenarios list for results.`
      );

      // Notify parent component to refresh scenarios
      if (onSimulationComplete) {
        onSimulationComplete();
      }
    } catch (error) {
      console.error("Quick build error:", error);
      toast.error("Failed to start quick build");
    }
  };

  const businessUnitOptions = useMemo(() => {
    return brandGeographies?.map((brandGeography) => ({
      value: brandGeography.brand,
      label: getBCPHierarchyLabel({
        brand: brandGeography.brand,
        category: "default",
        product: "default",
      }),
    }));
  }, [brandGeographies, getBCPHierarchyLabel]);

  const monthsMarks = [
    { value: 1, label: "1m" },
    { value: 3, label: "3m" },
    { value: 6, label: "6m" },
    { value: 9, label: "9m" },
    { value: 12, label: "12m" },
  ];

  const calculateDifferenceFromHistoricalSpend = useCallback(
    (percentage: number) => {
      if (percentage === 0) return 0;
      return historicalSpendBase * (1 + percentage / 100) - historicalSpendBase;
    },
    [historicalSpendBase]
  );

  const getBudgetMarkLabel = (percentage: number) => {
    const labelPercentage = percentage === -95 ? -100 : percentage;
    if (budgetMetricType === "percentage") {
      if (percentage > 0) return `+${percentage}%`;
      return `${labelPercentage}%`;
    }
    const budgetChange = calculateDifferenceFromHistoricalSpend(labelPercentage);
    const formattedCurrency = formatCurrency(budgetChange, ABBREVIATED_OPTION);

    if (percentage > 0) return `+${formattedCurrency}`;
    return formattedCurrency;
  };

  const budgetMarks = [
    { value: -95, label: getBudgetMarkLabel(-95) },
    { value: -50, label: getBudgetMarkLabel(-50) },
    { value: 0, label: getBudgetMarkLabel(0) },
    { value: 50, label: getBudgetMarkLabel(50) },
    { value: 100, label: getBudgetMarkLabel(100) },
  ];

  const scenarioTypeOptions = [
    { label: "Forecast", value: "budget" as const },
    { label: "Optimizer", value: "optimizer" as const },
  ];

  const budgetSliderMetricOptions = [
    { label: "%", value: "percentage" as const },
    { label: config.currency.getSymbol(), value: "amount" as const },
  ];

  const isSimulating =
    quickSimulateCreateScenario.isPending || runScenario.isPending || updateScenario.isPending;

  // Form controls should only be disabled when basic metadata is unavailable
  const isFormDisabled = isLoadingMetadata || !brandGeographies;

  // Simulate button should be disabled when any required data for simulation is missing
  const isSimulateDisabled =
    isFormDisabled ||
    !user?.email ||
    !dateRanges ||
    !historicalSpendsDTO ||
    !quickSimulateStartDate ||
    !quickSimulateEndDate ||
    !businessUnit ||
    !isDefined(maxAllowedBudget);

  const getBusinessUnitLabel = (businessUnit: string) => {
    const option = businessUnitOptions?.find((opt) => opt.value === businessUnit);
    return option?.label || businessUnit;
  };

  const budgetChange = useMemo(() => {
    if (!historicalSpendBase) return "";
    const sign = budgetPercentage > 0 ? "+" : "";
    if (budgetMetricType === "percentage") {
      return `${sign}${budgetPercentage}% from historical budget`;
    }
    return `${sign}${formatCurrency(calculateDifferenceFromHistoricalSpend(budgetPercentage))} from historical budget`;
  }, [
    historicalSpendBase,
    budgetPercentage,
    budgetMetricType,
    calculateDifferenceFromHistoricalSpend,
    formatCurrency,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <Stack
        direction="column"
        sx={{ backgroundColor: theme.palette.quickSimulate.backgroundColor }}
      >
        <Stack direction="row" spacing={3}>
          <Stack spacing={4} sx={{ px: 6, py: 3 }} width={400}>
            <Typography variant="h6" fontWeight={600} fontSize={24} textAlign="center">
              Quick Scenario
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={scenarioType}
              fullWidth
              sx={{ display: "flex", justifyContent: "center" }}
            >
              {scenarioTypeOptions.map((option) => (
                <ToggleButton
                  key={option.value}
                  value={option.value}
                  onClick={() => setScenarioType(option.value)}
                  disabled={isFormDisabled || isSimulating}
                >
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <FormControl fullWidth>
              <Typography variant="body2" fontWeight={600} mb={1} fontSize={14}>
                Business Unit
              </Typography>
              <StyledSelect
                value={businessUnit}
                onChange={(event) => handleBusinessUnitChange(event.target.value)}
                size="small"
                displayEmpty
                disabled={isFormDisabled || isSimulating || !brandGeographies?.length}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography sx={{ color: "text.primary", fontSize: "14px" }}>
                        Select an option
                      </Typography>
                    );
                  }
                  return getBusinessUnitLabel(selected);
                }}
              >
                {businessUnitOptions?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>

            <Box>
              <Typography fontWeight={600} variant="body2" mb={2} fontSize={14}>
                Duration:
              </Typography>
              <Box px={2}>
                <StyledSlider
                  value={months}
                  onChange={handleMonthsChange}
                  min={1}
                  max={12}
                  step={1}
                  marks={monthsMarks}
                  track={false}
                  disabled={isFormDisabled || isSimulating}
                />
              </Box>
            </Box>
            <Box height={110}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography fontWeight={600} variant="body2" fontSize={14}>
                  Budget Change:
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={budgetMetricType}
                  size="small"
                  sx={{ width: 70 }}
                >
                  {budgetSliderMetricOptions.map((option) => (
                    <ToggleButton
                      key={option.value}
                      value={option.value}
                      onClick={() => setBudgetMetricType(option.value)}
                      sx={{ height: 35 }}
                    >
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
              <Box px={2}>
                <StyledSlider
                  value={budgetPercentage}
                  onChange={handleBudgetChange}
                  min={-100}
                  max={100}
                  step={10}
                  marks={budgetMarks}
                  track={false}
                  disabled={isFormDisabled || isSimulating}
                />
              </Box>
              {budgetPercentage === -95 && (
                <Typography
                  variant="body2"
                  fontSize={12}
                  fontStyle="italic"
                  sx={{ color: "#FC9429" }}
                >
                  Budget set to minimum value of -95%
                </Typography>
              )}
            </Box>
          </Stack>
          <ScenarioSummary
            scenarioType={scenarioType}
            businessUnit={getBusinessUnitLabel(businessUnit)}
            months={months}
            startDate={quickSimulateStartDate}
            endDate={quickSimulateEndDate}
            budgetAmount={budgetAmount}
            formatCurrency={formatCurrency}
            budgetChange={budgetChange}
          />
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="flex-end" spacing={3} p={4}>
          <OutlinedButton
            onClick={onClose}
            fullWidth
            disabled={isSimulating}
            sx={{
              borderRadius: 4,
              textTransform: "none",
              fontWeight: 500,
              fontSize: 16,
              color: "text.secondary",
              borderColor: "grey.300",
            }}
          >
            Cancel
          </OutlinedButton>
          <PrimaryButton
            onClick={handleQuickSimulate}
            fullWidth
            disabled={isSimulateDisabled || isSimulating}
            sx={{
              borderRadius: 4,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            {isSimulating ? "Submitting" : "Submit Scenario"}
          </PrimaryButton>
        </Stack>
      </Stack>
    </Dialog>
  );
};
